import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronLeft, Search, Menu } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setSearchQuery } from '../../store/slices/searchSlice';

interface MobileBackNavProps {
  title?: string;
  showSearch?: boolean;
  onMobileSidebarToggle?: () => void;
}

const MobileBackNav = ({ 
  title, 
  showSearch = false,
  onMobileSidebarToggle
}: MobileBackNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchQuery = useSelector((state: RootState) => state.search.query);

  // Don't show on home page
  if (location.pathname === '/') {
    return null;
  }

  // Determine page title based on route
  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path.startsWith('/designers/')) return 'Designer';
    if (path === '/designers') return 'Explore Designers';
    if (path === '/insights') return 'Insights';
    if (path === '/account/portfolio') return 'My Archive';
    if (path === '/account/settings') return 'Settings';
    if (path === '/account/billing') return 'Billing';
    if (path === '/account/verification') return 'Verification';
    if (path === '/join') return 'Join';
    if (path === '/login') return 'Login';
    
    return '';
  };

  const pageTitle = getPageTitle();
  
  // Check if this is a parent page (no back button needed)
  const isParentPage = location.pathname === '/' || 
                       location.pathname === '/designers' || 
                       location.pathname.includes('/social-profile/');

  return (
    <div className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
      <div className="flex items-center h-14 px-4">
        {/* Parent Pages: Logo on the left, no back button */}
        {isParentPage ? (
          <>
            <Link to="/" className="flex items-center">
              <img 
                src="/Creatorsarchive1.2@4x.png" 
                alt="Creators Archive" 
                className="h-6 w-auto"
              />
            </Link>
            <div className="flex-1" /> {/* Spacer */}
          </>
        ) : (
          <>
            {/* Back Button for child pages */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-9 h-9 -ml-2 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-200"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6 text-gray-900" strokeWidth={2.5} />
            </button>

            {/* Title */}
            {pageTitle && (
              <h1 className="flex-1 text-center text-base font-semibold text-gray-900 px-4 truncate tracking-tight">
                {pageTitle}
              </h1>
            )}

            {/* Logo (if no title) */}
            {!pageTitle && (
              <Link to="/" className="flex-1 flex justify-center px-4">
                <img 
                  src="/Creatorsarchive1.2@4x.png" 
                  alt="Creators Archive" 
                  className="h-6 w-auto"
                />
              </Link>
            )}
          </>
        )}

        {/* Sidebar Toggle or Search Icon */}
        {onMobileSidebarToggle ? (
          <button
            onClick={onMobileSidebarToggle}
            className="flex items-center justify-center w-9 h-9 -mr-2 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-200"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
          </button>
        ) : showSearch ? (
          <button
            className="flex items-center justify-center w-9 h-9 -mr-2 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-200"
            aria-label="Search"
          >
            <Search className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
          </button>
        ) : !isParentPage ? (
          <div className="w-9" /> // Spacer for centering on child pages
        ) : null}
      </div>

      {/* Optional Search Bar */}
      {showSearch && (
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => dispatch(setSearchQuery(e.target.value))}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full border-0 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileBackNav;
